import { formatCurrency, formatPercent } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, computed, ElementRef, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBookMedical, faFileContract, faFileInvoiceDollar, faLaptopCode, faList, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { faChartPieSimple } from '@fortawesome/pro-solid-svg-icons';
import { NgbModal, NgbModalRef, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Table } from 'primeng/table';
import { DecimalPipe } from '@angular/common';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
Chart.register(...registerables, ChartDataLabels);

import { ConfirmationModalComponent } from "app/shared/form-elements/components/confirmation-modal/confirmation-modal.component";
import { BAService } from 'app/ba/ba.service';
import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { SecurityService } from 'app/core/security/security.service';
import { NotificationService } from 'app/notification.service';
import { PercentageService } from 'app/shared/form-elements/directives/masking/percentage.service';
import { UserJsVm } from 'app/shared/generated/Administration/Models/Users/UserJsVm';
import { BA_BudgetVm } from 'app/shared/generated/BA/Models/BA_BudgetVm';
import { PurchaseOrderListVm } from 'app/shared/generated/BA/Models/PurchaseOrderListVm';
import { FeatureFlagService } from 'app/shared/services/feature-flag.service';
import { TableTopLegendVm } from 'app/shared/table/table-top/table-top-legend/table-top-legendVm';
import { SystemMessageService } from 'app/core/system-message/system-message-service';
import { GlobalService } from 'app/shared/services/global.service';
import { SharedTableModule } from 'app/shared/standalone/shared-table/shared-table.module';
import { AvailableColumns, VisibleColsService } from 'app/shared/standalone/shared-table/visible-cols.service';
import { LazyTableInput } from 'app/shared/standalone/shared-table/lazy-table-input.interface';
import { LazyTableService } from 'app/shared/standalone/shared-table/lazy-table.service';
import { LazyLoadVm } from 'app/shared/standalone/shared-table/lazy-load-vm.interface';
import { ColorService } from 'app/shared/services/color.service';

@Component({
    selector: 'pcg-budget-pos-list'
    , templateUrl: './budget-pos-list.component.html'
    , styleUrls: ['./budget-pos-list.component.scss']
    , providers: [ DecimalPipe ]
    , host: { '(window:resize)': 'onResize()' }
    , changeDetection: ChangeDetectionStrategy.Eager
    , imports: [
        SharedTableModule
        , FontAwesomeModule
        , NgbTooltip        
    ]
})
export class BudgetPosListComponent implements OnInit {
    @ViewChild('budgetChart', { static: false }) budgetChart: ElementRef;

    //#region Injections
    httpClient = inject(HttpClient);
    cdr = inject(ChangeDetectorRef);
    ns = inject(NotificationService);
    baService = inject(BAService);
    route = inject(ActivatedRoute);
    router = inject(Router);
    sec = inject(SecurityService);
    ff = inject(FeatureFlagService);
    modalService = inject(NgbModal);
    sm = inject(SystemMessageService);
    decimalPipe = inject(DecimalPipe);
    lts = inject(LazyTableService);
    //#endregion

    //#region View Helpers
    canDelete = signal<boolean>(this.sec.hasModuleAccess([ BusinessAreaEnum.BA ], [ PermissionRole.SystemAdmin ]));
    isMobile = false;
    modal: NgbModalRef;

    readonly LABELS = {
		A: 'Allocations (A)'
        , B: 'Expenditures YTD (B)'
        , C: 'Remaining PO(s) Balance (C)'
        , D: 'Remaining PO(s) Percent (D)'
        , E: 'Available Budget (E)'
	};

	readonly COLORS = {
		A: 'rgb(10, 11, 51)'
        , B: 'rgb(149, 175, 200)'
        , C: 'rgb(63, 121, 235)'
        , D: 'rgb(139, 212, 232)'
        , E: 'rgb(93, 198, 137)'
	};

    kpiChart = signal<boolean>(true); // Used to toggle between displaying the chart or list of budget breakdowns
    //#endregion
    
    //#region Main Data
    user = signal<UserJsVm>(this.sec.getUser());

    budgetId = signal<number>(null);
	budgetSignal = computed(() => this.baService.budgetSignal());
    allocations = computed(() => this.budgetSignal()?.allocatedAmt ?? 0);
	expenditures = computed(() => this.budgetSignal()?.expenditures ?? 0);
	remainingBalance = computed(() => this.budgetSignal()?.pOsRemainingBalance ?? 0);
	remainingPercent = computed(() => PercentageService.calculatePercentage(this.remainingBalance(), this.allocations()));
	availableBudget = computed(() => this.allocations() - this.expenditures() - this.remainingBalance());
    //#endregion
    
    //#region Chart
	chartData = signal<Chart | undefined>(undefined);

	sectors = computed(() => {
		return {
			A: this.getDataSet('A', this.allocations())
            , B: this.getDataSet('B', this.expenditures())
            , C: this.getDataSet('C', this.remainingBalance())
            , D: this.getDataSet('D', this.remainingPercent())
            , E: this.getDataSet('E', this.availableBudget())
		};
	});
    //#endregion
    
    //#region FA Icons
    faFileContract = faFileContract;
    faFileInvoiceDollar = faFileInvoiceDollar;
    faBookMedical = faBookMedical;
    faList = faList;
    faChartPieSimple = faChartPieSimple;    
    faLaptopCode = faLaptopCode; // For PCG testing purposes only
    faRefresh = faRefresh;
    //#endregion

    //#region Table Info
    columnDefs = PurchaseOrderListVm.ColumnDefs;
    lazyLoadVm: LazyLoadVm;
    poList: PurchaseOrderListVm[] = [];
    availableColumns!: AvailableColumns[];
    visibleColumns!: AvailableColumns[];
    searchColumns!: string[];
    isLoading: boolean = true;
    searchValue: string | undefined;
    customExcelUrl = "api/BA/GetPoListExcel";

	filters = {
		isActive: true || 'true'
        , budgetId: this.baService.budgetSignal()?.budgetId
        , poId: null
        , useTestData: false
        , testAllocation: null
        , testMultiplier: null
        , testRandomize: false
	};
	initFilters = Object.assign({}, this.filters);

    legend: TableTopLegendVm[] = [
        {
            id: 0
            , colorCode: ColorService.getColor('--warning')
            , description: 'Available Purchase Order Funds are below notification levels'
        }
    ];

    tableDts = {
        root: {
            header: {
                lgPadding: "0 0 0.5rem 0"
            }
        }
    };

    lazyTableInput: LazyTableInput = {
        pageNumber: 1
        , pageSize: 100
        , sortField: "poNumber"
        , sortOrder: 1
    };
    //#endregion

    //#region Testing Logic
    testingMode = signal<boolean>(false);
    testPoList = signal<PurchaseOrderListVm[]>(null);
    testBudgetAllocation: number | null = null;
    testPoMultiplier: number | null = null;
    //#endregion

    constructor(title: Title) { title.setTitle("Budget POs"); }

    //#region Life cycle
    onResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

    ngOnInit() {
        this.isMobile = GlobalService.setIsMobile(window.innerWidth); 
        this.budgetId.set(this.route.snapshot.params.id);
        if (!(this.budgetId() > 0)) { window.location.href = '/ba/budgets/list'; }   
        this.getBudget();
    }

        // Check to make sure that the chart is properly drawn when user has selected to view the graph.
    ngDoCheck(): void {
        const isChartRendered = this.budgetChart?.nativeElement?.attributes?.style != null;

        if (
            this.kpiChart() 
            && !isChartRendered
        ) { this.createChart(); }
    }

	ngOnDestroy(): void { this.destroyChart(); }   
    //#endregion
    
    //#region Getter
    getBudget() {
        this.baService.getBudget(this.budgetId()).subscribe(
            r => {
                this.filters.budgetId = this.budgetId();
                this.initFilters = Object.assign({}, this.filters);   
                this.baService.budgetSignal.set(r);         
                this.getList();
                this.createChart();
            }
        );
    }

    getList() {
        this.httpClient.get(`api/BA/GetPoList` 
            + `?budgetId=${this.filters.budgetId}`
            + `&isActive=${this.filters.isActive}`
            + `&poId=${this.filters.poId}`
            + `&useTestData=${this.filters.useTestData}`
            + `&testAllocation=${this.filters.testAllocation}`
            + `&testMultiplier=${this.filters.testMultiplier}`
            + `&testRandomize=${this.filters.testRandomize}`
        ).subscribe((lst: PurchaseOrderListVm[]) => { 
            this.poList = lst;
            this.availableColumns = VisibleColsService.getAvailableCols(this.columnDefs);
            this.visibleColumns = this.availableColumns.filter(o => o.isVisible);
            this.searchColumns = this.availableColumns.filter(o => o.canSearch).map(o => o.field);
            this.isLoading = false;
            this.setExcelUrl();
        }); 
    }
    //#endregion

    //#region PO Actions
    addPo() { this.router.navigate([`/ba/budgets/pos/edit/0/${this.filters.budgetId}`]); }
    editPo(poId: number) { this.router.navigate([`/ba/budgets/pos/edit/${poId}/${this.filters.budgetId}`]); }
    createInvoice() { this.router.navigate([`/ba/budgets/invoices/edit/0/${this.filters.budgetId}`]); }
    addJournalEntry() { this.ns.showWarning('Add Journal Entry not yet implemented.'); return false; }

    deletePo(poId?: number) {
        this.modal = this.modalService.open(ConfirmationModalComponent);
        this.modal.componentInstance.modalRef = this.modal;
        this.modal.componentInstance.confirmationMessage = "Are you sure you want to delete this Product Order?";
        this.modal.componentInstance.isMobile = this.isMobile;
        
        this.modal.result.then((emittedValue) => {
            if (emittedValue) {
                this.sm.getHttpObservable(this,`api/BA/PurchaseOrder/DeletePO/${poId}`)
                    .subscribe((sm: any) => {
                        if (sm.isSuccessful) {
                            this.ns.showSuccess(sm.message);
                            this.getBudget();
                        } else {
                            this.ns.showError(sm.message);
                        }
                    }
                );
            }
        }).catch((err) => {
            // Prevent Uncaught (in promise) error
        });
    }
    //#endregion

    //#region View 
    viewInvoices(poId?: number) { 
        if (poId == null) { this.router.navigate([`/ba/budgets/invoices/list/${this.filters.budgetId}`]); }
        else { this.router.navigate([`/ba/budgets/invoices/list/${this.filters.budgetId}/${poId}`]); }
    }
    
    toggleKpiChart() {
        this.kpiChart.set(!this.kpiChart());
        this.createChart();
    }
    //#endregion

    //#region Chart Logic
	getDataSet(
        id: string
        , value: number
    ) {
		return {
			label: this.LABELS[id]
            , data: [value]
            , backgroundColor: this.COLORS[id]
            , hoverOffset: 4
		};
	}

	createChart() {
		this.destroyChart();
        if (
            !this.kpiChart() 
            || !this.budgetSignal()
        ) { return; }
        
        this.addDoughnutCenterText();
        
		const ctx = this.budgetChart?.nativeElement?.getContext('2d');
		const sectors = [this.sectors().B, this.sectors().C, this.sectors().E];
        const labels = sectors.map((d) => d.label);
        const values = sectors.map((d) => d.data[0]);
        const colors = sectors.map((d) => d.backgroundColor);

        const config: ChartConfiguration = {
            type: 'doughnut'
            , data: {
                labels: labels
                , datasets: [
                    {
                        data: values
                        , backgroundColor: colors
                        , datalabels: {
                            labels: {
                                name: {
                                    font: { size: 16, }
                                    , color: '#fff'
                                    , backgroundColor: '#404040'
                                    , borderRadius: 4
                                    , formatter: (val) => formatPercent(PercentageService.calculatePercentage(val, this.allocations()), 'en-US')
                                    , align: 'top'
                                }
                                , value: {
                                    color: '#404040'
                                    , backgroundColor: '#fff'
                                    , borderColor: '#404040'
                                    , borderWidth: 1
                                    , borderRadius: 4
                                    , formatter: (val) => formatCurrency((val ?? 0), 'en-US', '$')
                                    , align: 'bottom'
                                }
                            }
                        }
                        , hoverOffset: 4
                    }
                ]
            }
            , options: {
                responsive: true
                , maintainAspectRatio: false
                , plugins: {
                    legend: {
                        display: true
                        , position: 'left'
                        , align: 'start',
                        labels: {
                            useBorderRadius: true
                            , borderRadius: 4                             
                        }
                    }
                    , tooltip: {
                        callbacks: {
                            label: (context)=> '$' + (context?.formattedValue ?? '0.00')
                        }
                    }
                }
            }
        };

		if (ctx) {
            const chart = new Chart(ctx, config);
            chart.resize();
            chart.draw();
			this.chartData.set(chart);
            this.chartData().update();
		}
	}    

    addDoughnutCenterText() {
        const allocations = formatCurrency(this.allocations(), 'en-US', '$');
        const register = {
            id: 'textCenter'
            , beforeDatasetDraw(chart) {
                let ctx = chart.ctx;
                const { x, y } = chart.getDatasetMeta(0).data[0].getProps(['x', 'y'], true);
                const fontSize = (x / 500);                
                ctx.save();
                ctx.font = `bold ${fontSize}em sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('Allocations (A)', x, y - 10);
                ctx.font = `${fontSize}em sans-serif`;
                ctx.fillText(allocations, x, y + 10);
            }
        };

        Chart.unregister(register);
        Chart.register(register);
    }

	private destroyChart(): void {
		if (this.chartData()) {
            const budgetVm = this.budgetSignal();
            // Setting to null to force the graph to reload with the new budget signal.
            this.baService.budgetSignal.set(null);
            // This should Re-render the graph.
            this.baService.budgetSignal.set(budgetVm);
			this.chartData().destroy();
		}
	}
    //#endregion
    
    //#region Table Logic
    columnChange(event: any) {
        this.visibleColumns = event;
        this.visibleColumns = event.sort((a, b) => {
            return this.availableColumns.findIndex(p => p.field === a.field) 
                - this.availableColumns.findIndex(p => p.field === b.field);
        });
    }

    setExcelUrl() {
		this.customExcelUrl = `api/BA/GetPoListExcel` 
            + `?budgetId=${this.filters.budgetId}`
            + `&isActive=${this.filters.isActive}`
            + `&poId=${this.filters.poId}`
            + `&useTestData=${this.filters.useTestData}`
            + `&testAllocation=${this.filters.testAllocation}`
            + `&testMultiplier=${this.filters.testMultiplier}`
            + `&testRandomize=${this.filters.testRandomize}`;
	}

    clearFilters(
        table: Table
        , el: any
    ) {
        table.clear();
        this.searchValue = '';
        el.value = "";
        this.filters = Object.assign(this.filters, this.initFilters);
        this.getList();
    }

    exportList() { this.httpClient.get(this.customExcelUrl).subscribe((output: any) => { window.open(output); }); }
    //#endregion
}
